// DESCRIPTION: EventFilters — search + status + type toolbar for the events admin list.
// DESCRIPTION: Live debounced search is handled by the parent (EventList) via useDebounce.

import { __ } from '@wordpress/i18n';

/**
 * Filter/search toolbar for the events list.
 *
 * Search is controlled by `searchValue`/`onSearchChange` and fires on every
 * keystroke; the parent debounces it. Status and type changes fire immediately.
 *
 * @since PBOOKING_SINCE
 *
 * @param {Object}   props
 * @param {string}   props.searchValue       Live search input value.
 * @param {Function} props.onSearchChange    Called on every keystroke with the new value.
 * @param {string}   props.status            Current status filter value.
 * @param {Function} props.onStatusChange    Called with the new status value.
 * @param {string}   props.eventType         Current event type filter value.
 * @param {Function} props.onEventTypeChange Called with the new event type value.
 * @param {Function} props.onClear           Called when the user clears all filters.
 * @param {boolean}  props.hasActiveFilters  Whether any filter is active (shows the clear button).
 *
 * @return {JSX.Element} The EventFilters component.
 */
const EventFilters = ( {
	searchValue,
	onSearchChange,
	status,
	onStatusChange,
	eventType,
	onEventTypeChange,
	onClear,
	hasActiveFilters,
} ) => {
	const statusOptions = [
		{
			label: __( 'Active (Published + Draft)', 'nemtly-booking' ),
			value: 'publish,draft',
		},
		{ label: __( 'All statuses', 'nemtly-booking' ), value: 'any' },
		{ label: __( 'Published', 'nemtly-booking' ), value: 'publish' },
		{ label: __( 'Draft', 'nemtly-booking' ), value: 'draft' },
		{ label: __( 'Scheduled', 'nemtly-booking' ), value: 'future' },
		{ label: __( 'Trashed', 'nemtly-booking' ), value: 'trash' },
	];

	const typeOptions = [
		{ label: __( 'All types', 'nemtly-booking' ), value: '' },
		{ label: __( 'One-on-one', 'nemtly-booking' ), value: 'one-on-one' },
		{ label: __( 'Group', 'nemtly-booking' ), value: 'group' },
		{ label: __( 'Event', 'nemtly-booking' ), value: 'event' },
	];

	const controlClass =
		'nb-h-9 nb-px-3 nb-rounded-md nb-border nb-border-neutral-300 nb-bg-neutral-0 nb-text-sm nb-text-neutral-700 focus:nb-outline-none focus:nb-ring-2 focus:nb-ring-accent-500 focus:nb-border-accent-500 nb-transition-colors nb-duration-fast nb-cursor-pointer';

	// Search input uses explicit left padding (pl-9) for the leading icon instead
	// of px-3, so the placeholder text never overlaps the icon.
	// Utilities are marked important (!) because WordPress admin's unlayered
	// input[type="text"] rule would otherwise override Tailwind's layered padding/height.
	const inputClass =
		'!nb-h-9 !nb-pl-9 !nb-pr-3 !nb-rounded-md !nb-border !nb-border-neutral-300 !nb-bg-neutral-0 !nb-text-sm !nb-text-neutral-700 focus:!nb-outline-none focus:!nb-ring-2 focus:!nb-ring-accent-500 focus:!nb-border-accent-500 !nb-transition-colors !nb-duration-fast !nb-w-full';

	return (
		<div className="nb-flex nb-items-center nb-gap-3 nb-px-5 nb-py-3 nb-border-b nb-border-neutral-200 nb-bg-neutral-0 nb-flex-wrap nb-mb-4">
			{ /* Search */ }
			<div className="nb-relative nb-flex-1 nb-min-w-[200px] nb-max-w-sm">
				<div className="nb-absolute nb-inset-y-0 nb-left-0 nb-flex nb-items-center nb-pl-3 nb-pointer-events-none">
					<svg
						className="nb-w-4 nb-h-4 nb-text-neutral-400"
						fill="none"
						viewBox="0 0 16 16"
						aria-hidden="true"
					>
						<circle
							cx="7"
							cy="7"
							r="4.5"
							stroke="currentColor"
							strokeWidth="1.4"
						/>
						<path
							d="M10.5 10.5L14 14"
							stroke="currentColor"
							strokeWidth="1.4"
							strokeLinecap="round"
						/>
					</svg>
				</div>
				<input
					type="text"
					value={ searchValue }
					onChange={ ( e ) => onSearchChange( e.target.value ) }
					placeholder={ __( 'Search events…', 'nemtly-booking' ) }
					className={ inputClass }
					aria-label={ __( 'Search events', 'nemtly-booking' ) }
				/>
			</div>

			{ /* Status */ }
			<select
				value={ status }
				onChange={ ( e ) => onStatusChange( e.target.value ) }
				className={ controlClass }
				aria-label={ __( 'Filter by status', 'nemtly-booking' ) }
			>
				{ statusOptions.map( ( option ) => (
					<option key={ option.value } value={ option.value }>
						{ option.label }
					</option>
				) ) }
			</select>

			{ /* Type */ }
			<select
				value={ eventType }
				onChange={ ( e ) => onEventTypeChange( e.target.value ) }
				className={ controlClass }
				aria-label={ __( 'Filter by event type', 'nemtly-booking' ) }
			>
				{ typeOptions.map( ( option ) => (
					<option key={ option.value } value={ option.value }>
						{ option.label }
					</option>
				) ) }
			</select>

			{ /* Clear */ }
			{ hasActiveFilters && (
				<button
					type="button"
					onClick={ onClear }
					className="nb-text-sm nb-text-neutral-500 hover:nb-text-neutral-900 nb-underline nb-cursor-pointer nb-transition-colors nb-duration-fast"
				>
					{ __( 'Clear', 'nemtly-booking' ) }
				</button>
			) }
		</div>
	);
};

export default EventFilters;
